import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Key, Smartphone, Mail, Copy, Check, LogOut, History, AlertTriangle } from 'lucide-react';
import AppHeader from '../../components/AppHeader';
import BottomNav from '../../components/BottomNav';
import ErrorPage from '../../components/ErrorPage';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'https://drvivara.cloud/api' });
api.interceptors.request.use(c => { const t = localStorage.getItem('token') || sessionStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const SecuritySettings = () => {
  const { showSuccess, showError } = useNotification();
  const [twoFAStatus, setTwoFAStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [section, setSection] = useState('2fa');

  const [method, setMethod] = useState('authenticator');
  const [setupData, setSetupData] = useState(null);
  const [verifyToken, setVerifyToken] = useState('');
  const [disableToken, setDisableToken] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [showCodes, setShowCodes] = useState(false);
  const [codesCopied, setCodesCopied] = useState(false);

  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchStatus = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/2fa/status');
      setTwoFAStatus(res.data.data);
    } catch (err) { setError(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const res = await api.get('/sessions');
      setSessions(res.data.data || []);
    } catch {} finally { setLoadingSessions(false); }
  };

  const fetchHistory = async () => {
    setLoadingSessions(true);
    try {
      const res = await api.get('/sessions/history');
      setLoginHistory(res.data.data || []);
    } catch {} finally { setLoadingSessions(false); }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleSetup = async () => {
    try {
      const res = await api.post('/2fa/setup', { method });
      setSetupData(res.data.data);
      setBackupCodes(res.data.data?.backupCodes || []);
      setShowCodes(false);
    } catch (err) { showError(err.response?.data?.message || 'Setup failed'); }
  };

  const handleVerifyEnable = async () => {
    if (!verifyToken.trim()) { showError('Enter verification code'); return; }
    try {
      await api.post('/2fa/verify-enable', { token: verifyToken.trim() });
      showSuccess('2FA enabled!');
      setSetupData(null);
      setVerifyToken('');
      setShowCodes(true);
      fetchStatus();
    } catch (err) { showError(err.response?.data?.message || 'Verification failed'); }
  };

  const handleDisable = async () => {
    if (!disableToken.trim()) { showError('Enter your 2FA code to disable'); return; }
    try {
      await api.post('/2fa/disable', { token: disableToken.trim() });
      showSuccess('2FA disabled');
      setDisableToken('');
      setBackupCodes([]);
      setShowCodes(false);
      fetchStatus();
    } catch (err) { showError(err.response?.data?.message || 'Failed to disable'); }
  };

  const copyAllCodes = () => {
    navigator.clipboard?.writeText(backupCodes.join('\n'));
    setCodesCopied(true); setTimeout(() => setCodesCopied(false), 2000);
  };

  const handleLogoutOther = async () => {
    try {
      await api.post('/sessions/logout-other');
      showSuccess('Other sessions logged out');
      fetchSessions();
    } catch (err) { showError('Failed to logout other sessions'); }
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      await api.delete(`/sessions/${sessionId}`);
      showSuccess('Session terminated');
      setSessions(prev => prev.filter(s => s._id !== sessionId));
    } catch (err) { showError('Failed to terminate session'); }
  };

  if (error) return <ErrorPage message={error} onRetry={fetchStatus} />;

  return (
    <div className="bg-dark min-h-screen flex flex-col">
      <AppHeader title="Security Settings" />
      <div className="flex-1 p-4 pb-24 overflow-y-auto">
        <div className="flex gap-2 mb-4">
          {[{ id: '2fa', label: '2FA' }, { id: 'sessions', label: 'Sessions' }, { id: 'history', label: 'History' }].map(s => (
            <button key={s.id} onClick={() => { setSection(s.id); if (s.id !== '2fa') { fetchSessions(); if (s.id === 'history') fetchHistory(); } }} className={`px-4 py-2 rounded-lg text-sm font-medium flex-1 text-center ${section === s.id ? 'bg-gold text-dark' : 'bg-gray-800 text-gray-400'}`}>{s.label}</button>
          ))}
        </div>

        {section === '2fa' && (
          <div>
            {loading ? <p className="text-center text-gray-500 py-12">Loading...</p> : (
              <div className="bg-gray-800 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Shield size={28} className={twoFAStatus?.enabled ? 'text-green-400' : 'text-gray-500'} />
                  <div>
                    <h3 className="text-white font-bold">Two-Factor Authentication</h3>
                    <p className="text-xs text-gray-400">Status: {twoFAStatus?.enabled ? <span className="text-green-400 font-medium">Enabled ({twoFAStatus.method})</span> : <span className="text-yellow-400 font-medium">Not enabled</span>}</p>
                  </div>
                </div>

                {!twoFAStatus?.enabled && !setupData && (
                  <div>
                    <label className="text-xs text-gray-400 mb-2 block font-medium">Choose method:</label>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <button onClick={() => setMethod('authenticator')} className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition ${method === 'authenticator' ? 'bg-gold/20 border-gold text-gold' : 'bg-gray-700 border-gray-600 text-gray-400'}`}>
                        <Smartphone size={24} /> <span className="text-xs">Authenticator</span>
                      </button>
                      <button onClick={() => setMethod('email')} className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition ${method === 'email' ? 'bg-gold/20 border-gold text-gold' : 'bg-gray-700 border-gray-600 text-gray-400'}`}>
                        <Mail size={24} /> <span className="text-xs">Email OTP</span>
                      </button>
                    </div>
                    <button onClick={handleSetup} className="w-full bg-gold text-dark py-3 rounded-xl font-bold">Setup 2FA</button>
                  </div>
                )}

                {setupData && (
                  <div>
                    {setupData.qrCode && (
                      <div className="text-center mb-4">
                        <p className="text-xs text-gray-400 mb-2">Scan this QR code with your authenticator app:</p>
                        <img src={setupData.qrCode} alt="QR Code" className="mx-auto bg-white p-2 rounded-lg w-48 h-48" />
                        {setupData.secret && (
                          <div className="mt-3 bg-gray-700 rounded-lg p-3 flex items-center justify-between">
                            <code className="text-xs text-gray-300 break-all">{setupData.secret}</code>
                            <button onClick={() => { navigator.clipboard?.writeText(setupData.secret); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="text-gold shrink-0 ml-2">{copied ? <Check size={16} /> : <Copy size={16} />}</button>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mb-4">
                      <label className="text-xs text-gray-400 mb-1 block font-medium">Enter verification code from app:</label>
                      <input value={verifyToken} onChange={e => setVerifyToken(e.target.value)} className="w-full bg-gray-700 text-white rounded-xl p-3 text-sm border border-gray-600 focus:border-gold outline-none text-center text-lg tracking-widest" placeholder="000000" maxLength="6" />
                    </div>
                    <button onClick={handleVerifyEnable} className="w-full bg-gold text-dark py-3 rounded-xl font-bold">Verify & Enable</button>
                  </div>
                )}

                {twoFAStatus?.enabled && (
                  <div>
                    <div className="mb-4">
                      <label className="text-xs text-gray-400 mb-1 block font-medium">Enter 2FA code to disable:</label>
                      <input value={disableToken} onChange={e => setDisableToken(e.target.value)} className="w-full bg-gray-700 text-white rounded-xl p-3 text-sm border border-gray-600 focus:border-gold outline-none text-center" placeholder="000000" maxLength="6" />
                    </div>
                    <button onClick={handleDisable} className="w-full bg-red-500/20 text-red-400 py-3 rounded-xl font-medium border border-red-500/30">Disable 2FA</button>
                  </div>
                )}

                {showCodes && backupCodes.length > 0 && (
                  <div className="mt-4 bg-yellow-900/20 border border-yellow-600/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-yellow-400 font-bold text-sm flex items-center gap-1"><Key size={14} /> Backup Codes</h4>
                      <button onClick={copyAllCodes} className="text-xs text-gold flex items-center gap-1">{codesCopied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}</button>
                    </div>
                    <p className="text-xs text-yellow-400/70 mb-2">Save these in a secure place. Each code can be used only once.</p>
                    <div className="grid grid-cols-2 gap-1">
                      {backupCodes.map((code, i) => <code key={i} className="text-xs bg-gray-800 px-2 py-1 rounded text-gray-300 font-mono">{code}</code>)}
                    </div>
                    <p className="text-xs text-yellow-400/70 mt-2 flex items-center gap-1"><AlertTriangle size={12} /> These codes will not be shown again.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {section === 'sessions' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-bold">Active Sessions ({sessions.length})</h3>
              <button onClick={handleLogoutOther} className="text-xs bg-red-500/20 text-red-400 px-3 py-1.5 rounded-lg font-medium">Logout Others</button>
            </div>
            {loadingSessions ? (
              <p className="text-center text-gray-500 py-8">Loading...</p>
            ) : sessions.length > 0 ? (
              <div className="space-y-3">
                {sessions.map(s => (
                  <div key={s._id} className="bg-gray-800 rounded-xl p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <Smartphone size={20} className="text-gold mt-1 shrink-0" />
                        <div>
                          <p className="text-white text-sm font-medium">{s.deviceInfo?.device || 'Unknown Device'}</p>
                          <p className="text-xs text-gray-400">{s.deviceInfo?.browser} · {s.deviceInfo?.os}</p>
                          <p className="text-xs text-gray-500">{s.ip} · Logged in {new Date(s.createdAt).toLocaleString()}</p>
                          {s.lastActive && <p className="text-xs text-gray-500">Last active: {new Date(s.lastActive).toLocaleString()}</p>}
                          {s.isCurrent && <span className="text-xs text-green-400 font-medium mt-1 inline-block">Current session</span>}
                        </div>
                      </div>
                      {!s.isCurrent && (
                        <button onClick={() => handleTerminateSession(s._id)} className="text-red-400 p-2 hover:bg-red-500/20 rounded-lg transition" title="Terminate">
                          <LogOut size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12"><Smartphone size={48} className="mx-auto mb-3 opacity-30" /><p>No active sessions</p></div>
            )}
          </div>
        )}

        {section === 'history' && (
          <div>
            <h3 className="text-white font-bold mb-4"><History size={16} className="inline mr-1" /> Login History</h3>
            {loadingSessions ? (
              <p className="text-center text-gray-500 py-8">Loading...</p>
            ) : loginHistory.length > 0 ? (
              <div className="space-y-2">
                {loginHistory.map((h, i) => (
                  <div key={h._id || i} className="bg-gray-800 rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <p className="text-white text-xs font-medium">{h.deviceInfo?.device || h.device || 'Unknown'}</p>
                      <p className="text-xs text-gray-400">{h.ip} · {h.deviceInfo?.browser || h.browser}</p>
                    </div>
                    <span className="text-xs text-gray-500 shrink-0">{new Date(h.createdAt || h.loginTime).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12"><History size={48} className="mx-auto mb-3 opacity-30" /><p>No login history</p></div>
            )}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default SecuritySettings;
